import React, { useMemo } from "react";
import { Card, Statistic, Row, Col, Typography, Tooltip } from "antd";
import {
  EnvironmentOutlined,
  ClockCircleOutlined,
  ShoppingOutlined,
  CarOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ILatLng, calculateRouteDistance, estimateTravelTime } from "../../../utils/geo/haversine";
import styles from "./style.module.scss";

const { Text } = Typography;

export interface ITripRouteSummaryProps {
  storeLocation?: ILatLng;
  orderLocations: ILatLng[];
  orderCount: number;
  avgSpeedKmh?: number;
  showCard?: boolean;
}

const TripRouteSummary: React.FC<ITripRouteSummaryProps> = ({
  storeLocation,
  orderLocations,
  orderCount,
  avgSpeedKmh = 30,
  showCard = true,
}) => {
  const { t } = useTranslation();

  const routeStats = useMemo(() => {
    const path: ILatLng[] = [];

    if (storeLocation) {
      path.push(storeLocation);
    }

    orderLocations.forEach((loc) => {
      if (loc) {
        path.push(loc);
      }
    });

    // Add return trip to store (close the loop)
    if (storeLocation && orderLocations.length > 0) {
      path.push(storeLocation);
    }

    if (path.length < 2) {
      return {
        totalDistance: 0,
        estimatedTime: 0,
        formattedDistance: "0",
        formattedTime: "0",
      };
    }

    const totalDistance = calculateRouteDistance(path);
    const estimatedTime = estimateTravelTime(totalDistance, avgSpeedKmh);

    return {
      totalDistance,
      estimatedTime,
      formattedDistance: totalDistance.toFixed(2),
      formattedTime: Math.ceil(estimatedTime).toString(),
    };
  }, [storeLocation, orderLocations, avgSpeedKmh]);

  const summaryContent = (
    <Row gutter={[16, 16]} className={styles.statsRow}>
      <Col xs={12} sm={6}>
        <Statistic
          title={
            <span className={styles.statTitle}>
              <ShoppingOutlined className={styles.icon} />
              {t("orders")}
            </span>
          }
          value={orderCount}
          valueStyle={{ color: "#3458a4", fontSize: 24 }}
        />
      </Col>

      <Col xs={12} sm={6}>
        <Tooltip title={t("total_route_distance")}>
          <Statistic
            title={
              <span className={styles.statTitle}>
                <EnvironmentOutlined className={styles.icon} />
                {t("distance")}
              </span>
            }
            value={routeStats.formattedDistance}
            suffix={t("km")}
            valueStyle={{ color: "#52c41a", fontSize: 24 }}
            precision={2}
          />
        </Tooltip>
      </Col>

      <Col xs={12} sm={6}>
        <Tooltip title={`${t("estimated_at_speed")} ${avgSpeedKmh} ${t("km_per_hour")}`}>
          <Statistic
            title={
              <span className={styles.statTitle}>
                <ClockCircleOutlined className={styles.icon} />
                {t("estimated_time")}
              </span>
            }
            value={routeStats.formattedTime}
            suffix={t("minutes")}
            valueStyle={{ color: "#fa8c16", fontSize: 24 }}
          />
        </Tooltip>
      </Col>

      <Col xs={12} sm={6}>
        <Tooltip title={t("avg_speed")}>
          <Statistic
            title={
              <span className={styles.statTitle}>
                <CarOutlined className={styles.icon} />
                {t("avg_speed")}
              </span>
            }
            value={avgSpeedKmh}
            suffix={t("km_h")}
            valueStyle={{ color: "#722ed1", fontSize: 24 }}
          />
        </Tooltip>
      </Col>
    </Row>
  );

  if (!showCard) {
    return <div className={styles.summaryContainer}>{summaryContent}</div>;
  }

  return (
    <Card className={styles.summaryCard} size="small">
      <div className={styles.cardHeader}>
        <Text strong>{t("route_summary")}</Text>
      </div>
      {summaryContent}
    </Card>
  );
};

export default React.memo(TripRouteSummary);
